import React, { useEffect, useId, useRef, useState } from 'react'

import { api } from '~/lib/api'
import { formatMoney } from '~/lib/format'
import { Alert } from '~/components/ui/Alert'

/**
 * The public wedding travel fee calculator. The server works every number out
 * (ToolsController#estimate, TravelFeeEstimate); this is the form and the
 * breakdown. A drive is looked up once; after that, changing the costs, nights
 * or shooters re-prices the same miles, so Google isn't asked again.
 *
 * The address bar always holds the current result (miles included), so a
 * copied link opens the same breakdown without another lookup.
 */
const ENDPOINT = '/tools/travel-fee-calculator'
// Short names for the link's query string.
const COST_PARAMS = { hotel: 'hotel', meal: 'meal', rental_car: 'car', airfare: 'airfare', mileage_rate: 'mileage', travel_day: 'day' }
const COST_HINTS = { travel_day: 'A day spent traveling: your day rate, or what you pay a shooter.' }

const money = (cents) => formatMoney(cents / 100)
const plural = (count, one, many) => `${count} ${count === 1 ? one : many}`

function initialForm(defaults, lookups) {
  const params = new URLSearchParams(window.location.search)
  const costs = Object.fromEntries(Object.entries(defaults.costs).map(([key, value]) => [key, params.get(COST_PARAMS[key]) ?? String(value)]))
  const from = params.get('from') ?? ''
  const venue = params.get('venue') ?? ''
  return {
    mode: lookups && (from || venue || !params.get('miles')) ? 'places' : 'miles',
    from,
    venue,
    miles: params.get('miles') ?? '',
    nights: params.get('nights') ?? '0',
    shooters: params.get('shooters') ?? '1',
    markup_percent: params.get('markup') ?? String(defaults.markup_percent),
    costs,
  }
}

/** A drive that came with the link: the places as typed, and the miles already found for them. */
function tripFromLink(form) {
  const params = new URLSearchParams(window.location.search)
  if (form.mode !== 'places' || !form.from || !form.venue || !params.get('miles')) return null
  return { miles: Number(params.get('miles')), estimated: params.get('estimated') === '1', from: form.from, venue: form.venue, typedFrom: form.from, typedVenue: form.venue }
}

function linkFor(form, trip, defaults) {
  const params = new URLSearchParams()
  if (form.mode === 'places' && trip) {
    params.set('from', trip.typedFrom)
    params.set('venue', trip.typedVenue)
    params.set('miles', String(trip.miles))
    if (trip.estimated) params.set('estimated', '1')
  } else {
    params.set('miles', form.miles)
  }
  if (form.nights !== '0') params.set('nights', form.nights)
  if (form.shooters !== '1') params.set('shooters', form.shooters)
  if (form.markup_percent !== String(defaults.markup_percent)) params.set('markup', form.markup_percent)
  Object.entries(form.costs).forEach(([key, value]) => {
    if (value !== String(defaults.costs[key])) params.set(COST_PARAMS[key], value)
  })
  return `${window.location.pathname}?${params}`
}

export default function TravelFeeCalculator({ lookups, defaults }) {
  const [form, setForm] = useState(() => initialForm(defaults, lookups))
  const [trip, setTrip] = useState(() => tripFromLink(form))
  const [result, setResult] = useState(null)
  const [errors, setErrors] = useState({})
  const [notice, setNotice] = useState(null)
  const [pending, setPending] = useState(false)
  const [copied, setCopied] = useState(null)
  const latest = useRef(0)
  const resultRef = useRef(null)
  const started = useRef(false)

  const placesMatchTrip = trip && trip.typedFrom === form.from && trip.typedVenue === form.venue
  const canReprice = form.mode === 'miles' ? form.miles.trim() !== '' : Boolean(placesMatchTrip)

  function common() {
    return { nights: form.nights, shooters: form.shooters, markup_percent: form.markup_percent, ...form.costs }
  }

  // A known drive: typed miles, or the places' drive found earlier.
  function byMiles() {
    return form.mode === 'miles' ? { miles: form.miles } : { miles: String(trip.miles), estimated: trip.estimated }
  }

  async function calculate(body, { lookup = false, scroll = false } = {}) {
    const id = ++latest.current
    setPending(true)
    try {
      const answer = await api.post(ENDPOINT, body)
      if (id !== latest.current) return
      const found = lookup ? { miles: answer.miles, estimated: answer.estimated, from: answer.from, venue: answer.venue, typedFrom: body.from, typedVenue: body.venue } : trip
      if (lookup) setTrip(found)
      setResult(answer)
      setErrors({})
      setNotice(null)
      setCopied(null)
      window.history.replaceState(null, '', linkFor(form, found, defaults))
      if (scroll && window.innerWidth < 1024) resultRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' })
    } catch (error) {
      if (id !== latest.current) return
      if (error.status === 422) {
        setErrors(error.fieldErrors)
        setNotice(null)
      } else {
        setNotice({ message: error.message, typeMiles: Boolean(error.body?.type_miles) })
      }
    } finally {
      if (id === latest.current) setPending(false)
    }
  }

  function submit(event) {
    event.preventDefault()
    if (form.mode === 'places' && !placesMatchTrip) {
      calculate({ ...common(), from: form.from, venue: form.venue }, { lookup: true, scroll: true })
    } else {
      calculate({ ...common(), ...byMiles() }, { scroll: true })
    }
  }

  // Re-price as the costs, nights, shooters or typed miles change, once there's a drive to price.
  const repriceKey = JSON.stringify([common(), form.mode, form.mode === 'miles' ? form.miles : null])
  useEffect(() => {
    if (!started.current) {
      started.current = true
      if (canReprice) calculate({ ...common(), ...byMiles() }) // opened from a link
      return undefined
    }
    if (!result || !canReprice) return undefined
    const timer = setTimeout(() => calculate({ ...common(), ...byMiles() }), 400)
    return () => clearTimeout(timer)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [repriceKey])

  const set = (field) => (value) => {
    setForm((current) => ({ ...current, [field]: value }))
    setErrors((current) => ({ ...current, [field]: undefined }))
  }
  const setCost = (key) => (value) => {
    setForm((current) => ({ ...current, costs: { ...current.costs, [key]: value } }))
    setErrors((current) => ({ ...current, [key]: undefined }))
  }
  function switchMode(mode) {
    setForm((current) => ({ ...current, mode, miles: mode === 'miles' && !current.miles && trip ? String(trip.miles) : current.miles }))
    setNotice(null)
  }

  async function copyLink() {
    try {
      await navigator.clipboard.writeText(window.location.href)
      setCopied('Link copied')
    } catch {
      setCopied("Copy this page's address from your browser to share it.")
    }
  }

  const error = (field) => errors[field]?.[0]
  const invalid = Object.values(errors).some(Boolean)
  const stale = result && (invalid ? 'Fix the marked boxes to update this.' : form.mode === 'places' && !placesMatchTrip ? 'The places have changed. Calculate again to find the new drive.' : null)

  return (
    <div className="grid gap-8 lg:grid-cols-[minmax(0,1fr)_minmax(0,26rem)] items-start">
      <form onSubmit={submit} noValidate data-turbo="false" aria-label="Travel fee calculator" className="card space-y-6">
        {lookups ? (
          <div role="radiogroup" aria-label="How to give the distance" className="flex flex-wrap gap-1 rounded-control border border-line bg-surface-muted p-1 w-fit max-w-full">
            {[['places', 'Look up the drive'], ['miles', 'Type the miles']].map(([value, label]) => (
              <button key={value} type="button" role="radio" aria-checked={form.mode === value} onClick={() => switchMode(value)}
                className={`min-h-10 px-4 text-sm font-semibold rounded-control transition-colors ${form.mode === value ? 'bg-primary text-on-primary shadow-pill' : 'text-ink-muted hover:text-primary'}`}>
                {label}
              </button>
            ))}
          </div>
        ) : null}

        {form.mode === 'places' ? (
          <div className="grid gap-4 sm:grid-cols-2">
            <TextInput label="Starting from" value={form.from} onChange={set('from')} placeholder="Nashville, TN" autoComplete="address-level2"
              hint="Where you or your shooter leave from. A city or ZIP is enough." error={error('from')} />
            <TextInput label="The wedding" value={form.venue} onChange={set('venue')} placeholder="Asheville, NC" autoComplete="off"
              hint="The venue's city, ZIP or address." error={error('venue')} />
          </div>
        ) : (
          <NumberInput className="sm:max-w-xs" label="One-way driving distance" value={form.miles} onChange={set('miles')} suffix="miles"
            step="1" hint="From where you leave to the venue." error={error('miles')} />
        )}

        <div className="grid gap-4 grid-cols-2 sm:grid-cols-3">
          <SelectInput className="col-span-2 sm:col-span-1" label="Nights before the wedding" value={form.nights} onChange={set('nights')} error={error('nights')}
            hint="Filming a rehearsal dinner or welcome party too."
            options={Array.from({ length: defaults.max_nights + 1 }, (_, n) => [String(n), n === 0 ? 'None' : plural(n, 'night', 'nights')])} />
          <SelectInput label="Shooters" value={form.shooters} onChange={set('shooters')} error={error('shooters')}
            options={Array.from({ length: defaults.max_shooters }, (_, i) => [String(i + 1), String(i + 1)])} />
          <NumberInput label="Markup for the couple" value={form.markup_percent} onChange={set('markup_percent')} suffix="%" step="1"
            error={error('markup_percent')} />
        </div>

        <fieldset>
          <legend className="section-title">Your costs</legend>
          <p className="form-hint mt-1 mb-4">Example costs to start from. Change them to yours.</p>
          <div className="grid gap-4 grid-cols-2 sm:grid-cols-3">
            {Object.entries(defaults.labels).map(([key, label]) => (
              <NumberInput key={key} label={label} value={form.costs[key]} onChange={setCost(key)} prefix="$" step="0.01"
                hint={COST_HINTS[key]} error={error(key)} />
            ))}
          </div>
        </fieldset>

        <div className="space-y-3">
          <button type="submit" className="btn-primary btn-lg w-full sm:w-auto" disabled={pending}>
            {pending ? 'Working…' : 'Calculate'}
          </button>
          {notice ? (
            <Alert tone="warning">
              {notice.message}
              {notice.typeMiles && form.mode === 'places' ? (
                <button type="button" className="ml-2 underline font-semibold" onClick={() => switchMode('miles')}>Type the miles</button>
              ) : null}
            </Alert>
          ) : null}
        </div>
      </form>

      <div ref={resultRef} className="lg:sticky lg:top-6 scroll-mt-6" aria-live="polite">
        {result ? (
          <Breakdown result={result} trip={form.mode === 'places' ? trip : null} stale={stale} copied={copied} onCopy={copyLink} name={defaults.name} />
        ) : (
          <div className="card text-ink-muted space-y-2">
            <h2 className="section-title">Your travel fee</h2>
            <p>{form.mode === 'places' ? 'Enter where you start and where the wedding is, then Calculate.' : 'Enter the miles, then Calculate.'}</p>
            <p className="text-sm">You'll see the distance band, every cost, the travel per shooter and the couple's quote.</p>
          </div>
        )}
      </div>
    </div>
  )
}

function Breakdown({ result, trip, stale, copied, onCopy, name }) {
  const base = result.lines.filter((line) => !line.extra)
  const extras = result.lines.filter((line) => line.extra)
  const miles = Number(result.miles).toLocaleString('en-US', { maximumFractionDigits: 1 })

  return (
    <div className={`card space-y-5 transition-opacity ${stale ? 'opacity-60' : ''}`}>
      <div>
        <h2 className="section-title">Your travel fee</h2>
        <p className="mt-1 text-sm text-ink-muted break-words">
          {trip ? `${trip.from} to ${trip.venue}: ` : ''}{miles} miles one way
          {result.estimated ? ' (estimated: Google Maps found no driving route, so this is a rough figure from the straight-line distance)' : ''}
        </p>
        <p className="mt-2 flex flex-wrap items-center gap-2">
          <span className="badge-brand">{result.band.label}</span>
          <span className="text-sm text-ink-muted">{result.band.distance}</span>
        </p>
        {stale ? <p className="mt-2 text-sm text-ink">{stale}</p> : null}
      </div>

      <div className="grid gap-3 grid-cols-2">
        <div className="stat-tile p-4">
          <p className="stat-label">Quote the couple</p>
          <p className="stat-value">{money(result.quote_cents)}</p>
          <p className="form-hint">Travel for {plural(result.shooters, 'shooter', 'shooters')}, with your {result.markup_percent}% markup.</p>
        </div>
        <div className="stat-tile p-4">
          <p className="stat-label">Travel per shooter</p>
          <p className="stat-value">{money(result.per_shooter_cents)}</p>
          <p className="form-hint">What each shooter's trip costs, rounded up to the next $10.</p>
        </div>
      </div>

      <div className="space-y-4 text-sm">
        <Lines title="For the wedding day, each shooter" lines={base} mileageRate={result.mileage_rate_cents}
          empty={`Nothing extra: a ${result.band.label.toLowerCase()} needs no travel for the wedding day.`} />
        {result.nights > 0 ? (
          <Lines title={`Each night before the wedding (${plural(result.nights, 'night', 'nights')})`} lines={extras} mileageRate={result.mileage_rate_cents}
            empty={`Nothing extra: a ${result.band.label.toLowerCase()} adds nothing for a night before.`} />
        ) : null}
        <p className="flex justify-between gap-3 border-t border-line pt-3 text-ink">
          <span>The trip, per shooter</span>
          <span>{money(result.travel_cents)}</span>
        </p>
      </div>

      <div className="space-y-2">
        <button type="button" className="btn-secondary w-full" onClick={onCopy}>Copy a link to this result</button>
        {copied ? <p className="form-hint text-center" role="status">{copied}</p> : null}
      </div>

      <p className="text-sm text-ink-muted border-t border-line pt-4">
        Run a studio? <a href="/for-studios" className="text-primary underline">{name}</a> works this out for every shooter on every job, from their own home.
      </p>
    </div>
  )
}

function Lines({ title, lines, mileageRate, empty }) {
  return (
    <div>
      <h3 className="font-semibold text-ink mb-2">{title}</h3>
      {lines.length ? (
        <ul className="space-y-1.5">
          {lines.map((line) => (
            <li key={line.label} className="flex justify-between gap-3">
              <span className="text-ink-muted min-w-0">
                {line.label}
                <span className="block text-xs">
                  {line.miles != null ? `${line.miles} miles × ${money(mileageRate)}` : `${line.quantity} × ${money(line.unit_cents)}`}
                </span>
              </span>
              <span className="text-ink shrink-0">{money(line.total_cents)}</span>
            </li>
          ))}
        </ul>
      ) : <p className="text-ink-muted">{empty}</p>}
    </div>
  )
}

function Labeled({ id, label, hint, error, className = '', children }) {
  return (
    <div className={className}>
      <label htmlFor={id} className="form-label">{label}</label>
      {children}
      {hint ? <p id={`${id}-hint`} className="form-hint">{hint}</p> : null}
      {error ? <p id={`${id}-error`} className="form-hint text-red-600">{error}</p> : null}
    </div>
  )
}

function describedBy(id, hint, error) {
  return [hint ? `${id}-hint` : null, error ? `${id}-error` : null].filter(Boolean).join(' ') || undefined
}

function TextInput({ label, value, onChange, hint, error, className, ...props }) {
  const id = useId()
  return (
    <Labeled id={id} label={label} hint={hint} error={error} className={className}>
      <input id={id} type="text" className="input-form-field" value={value} onChange={(e) => onChange(e.target.value)}
        aria-describedby={describedBy(id, hint, error)} aria-invalid={error ? true : undefined} maxLength={200} {...props} />
    </Labeled>
  )
}

function NumberInput({ label, value, onChange, prefix, suffix, hint, error, className, ...props }) {
  const id = useId()
  return (
    <Labeled id={id} label={label} hint={hint} error={error} className={className}>
      <div className="relative">
        {prefix ? <span className="pointer-events-none absolute inset-y-0 left-0 flex items-center pl-3 text-ink-muted" aria-hidden="true">{prefix}</span> : null}
        <input id={id} type="number" inputMode="decimal" min="0" className={`input-form-field ${prefix ? 'pl-7' : ''} ${suffix ? 'pr-16' : ''}`}
          value={value} onChange={(e) => onChange(e.target.value)} aria-describedby={describedBy(id, hint, error)}
          aria-invalid={error ? true : undefined} {...props} />
        {suffix ? <span className="pointer-events-none absolute inset-y-0 right-0 flex items-center pr-3 text-sm text-ink-muted" aria-hidden="true">{suffix}</span> : null}
      </div>
    </Labeled>
  )
}

function SelectInput({ label, value, onChange, options, hint, error, className }) {
  const id = useId()
  return (
    <Labeled id={id} label={label} hint={hint} error={error} className={className}>
      <select id={id} className="input-form-field" value={value} onChange={(e) => onChange(e.target.value)}
        aria-describedby={describedBy(id, hint, error)} aria-invalid={error ? true : undefined}>
        {options.map(([optionValue, optionLabel]) => <option key={optionValue} value={optionValue}>{optionLabel}</option>)}
      </select>
    </Labeled>
  )
}
